import { Component } from '@angular/core';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, shareReplay, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigRepository } from '../../../../../shared/repositories/config.repository';
import { Router } from '@angular/router';
import { BreakpointService } from '../../../../../shared/services/breakpoint.service';
import { select } from '@ngneat/elf';
import { Outlet } from '../../../../../api/v1/models/outlet';
import { zeroMaskFromTimeInterval } from '../../../../../shared/utils/get-time-intervals';

interface Schedule {
  open: string,
  close: string,
  dayKey: string,
  isOpen: boolean,
}

@Component({
  selector: 'app-resto-picker',
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    NgClass,
    AsyncPipe,
  ],
  templateUrl: './resto-picker.component.html',
  styleUrls: ['./resto-picker.component.scss']
})
export class RestoPickerComponent {
  constructor(
    private configRepository: ConfigRepository,
    private router: Router,
    public breakPoint: BreakpointService
  ) {
    this.router.events.subscribe(() => this.isRouteChange$.next(null))
  }

  private isRouteChange$ = new ReplaySubject(1);
  private urlByHide: string = '/outlets';

  isShowModal: boolean = false;

  activeOutletId$: Observable<number | undefined> = this.configRepository.activeOutletId$;
  address$: Observable<string | null> = this.configRepository.address$.pipe(
    shareReplay({ refCount: false, bufferSize: 1 }),
  );
  noOutlets$: Observable<boolean> = this.configRepository.outlets$.pipe(select(outlets => !outlets.length));

  isUserClose$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isUserChoose$: Observable<boolean> = this.configRepository.isUserChooseOutlet$;

  isShowDialog$: Observable<boolean> = combineLatest([
    this.isUserClose$,
    this.isUserChoose$,
    this.isRouteChange$,
  ]).pipe(
    map(([isUserClose, isUserChoose]) => {
      const isHideByUrl: boolean = this.router.url === this.urlByHide;
      return !isUserChoose && !isUserClose && !isHideByUrl;
    })
  )

  onCancel(): void {
    this.isUserClose$.next(true);
  }

  onOpen() {
    this.isShowModal = true;
  }

  onClose() {
    this.isShowModal = false;
  }

  public outletsSchedule: { [key: string]: Schedule | null };
  public outlets$: Observable<Outlet[]> = this.configRepository.outlets$.pipe(
    tap((outlets: Outlet[]) => {
      this.outletsSchedule = {};
      outlets.forEach((outlet: Outlet) => {
        this.outletsSchedule[outlet.storeId] = this.getOutletSchedule(outlet);
      })
    })
  );

  onChangeMenu(id: number): void {
    if (id === this.configRepository.activeOutletId) { return }
    this.configRepository.setActiveOutlet(id);
    this.configRepository.setUserChooseState(true);
    this.isShowModal = false;
  }


  private getOutletSchedule(outlet: Outlet): Schedule | null {
    let schedule: Schedule | null = null;
    const currentDayName: string = new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(new Date());

    if (!outlet.workingIntervals[currentDayName]) { return null }

    const dayHours = outlet.workingIntervals[currentDayName];
    schedule = {
      dayKey: currentDayName,
      open: zeroMaskFromTimeInterval(dayHours.at(0)?.open),
      close: zeroMaskFromTimeInterval(dayHours.at(-1)?.close),
      isOpen: this.configRepository.getOpenStateOutlet(dayHours),
    }

    return schedule;
  }
}
